import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type SyntheticEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { api, unwrap } from "@/api/client";
import { fetchSession, pathForStep, SESSION_KEY } from "@/auth/session";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { errorMessage } from "@/i18n";

import { AuthLayout } from "./auth-layout";

/**
 * Setting up an authenticator app: show the QR code and key, confirm with a
 * code, then show the recovery codes once.
 */
export function SetUpCodesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState(false);

  const setup = useMutation({
    mutationFn: async () => unwrap(await api.POST("/api/v1/auth/mfa/setup")),
  });
  const enable = useMutation({
    mutationFn: async () =>
      unwrap(await api.POST("/api/v1/auth/mfa/enable", { body: { code: code.trim() } })),
  });
  const finish = useMutation({
    mutationFn: fetchSession,
    onSuccess: (next) => {
      queryClient.setQueryData(SESSION_KEY, next);
      void navigate(next ? pathForStep(next.nextStep) : "/sign-in", { replace: true });
    },
  });

  if (enable.data) {
    const codes = enable.data.recoveryCodes;
    return (
      <AuthLayout title={t("setUpCodes.recoveryTitle")} intro={t("setUpCodes.recoveryIntro")}>
        <ul className="grid grid-cols-2 gap-2 rounded-md bg-muted p-4 font-mono">
          {codes.map((recovery) => (
            <li key={recovery}>{recovery}</li>
          ))}
        </ul>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button
            variant="secondary"
            onClick={() => {
              void navigator.clipboard.writeText(codes.join("\n")).then(() => {
                setCopied(true);
              });
            }}
          >
            {copied ? t("setUpCodes.copied") : t("setUpCodes.copy")}
          </Button>
          <Button
            onClick={() => {
              finish.mutate();
            }}
          >
            {t("setUpCodes.done")}
          </Button>
        </div>
      </AuthLayout>
    );
  }

  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    if (code.trim()) {
      enable.mutate();
    }
  };

  return (
    <AuthLayout title={t("setUpCodes.title")} intro={t("setUpCodes.intro")}>
      <ol className="flex list-decimal flex-col gap-5 pl-5">
        <li>{t("setUpCodes.step1")}</li>
        <li>
          <p>{t("setUpCodes.step2")}</p>
          {setup.data ? (
            <div className="mt-3 flex flex-col gap-3">
              <img
                src={setup.data.qrCode}
                alt={t("setUpCodes.qrAlt")}
                className="size-48 rounded-md bg-white p-2"
              />
              <p className="text-sm">
                {t("setUpCodes.key")}:{" "}
                <code className="break-all rounded bg-muted px-1">{setup.data.secret}</code>
              </p>
            </div>
          ) : (
            <Button
              className="mt-3"
              variant="secondary"
              disabled={setup.isPending}
              onClick={() => {
                setup.mutate();
              }}
            >
              {t("setUpCodes.step2")}
            </Button>
          )}
        </li>
        <li>
          <form noValidate className="flex flex-col gap-4" onSubmit={submit}>
            <p>{t("setUpCodes.step3")}</p>
            {enable.isError ? <Alert tone="error">{errorMessage(enable.error)}</Alert> : null}
            <Field
              label={t("code.label")}
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              disabled={!setup.data}
              onChange={(event) => {
                setCode(event.target.value);
              }}
            />
            <Button type="submit" disabled={!setup.data || enable.isPending}>
              {t("setUpCodes.submit")}
            </Button>
          </form>
        </li>
      </ol>
      {setup.isError ? <Alert tone="error">{errorMessage(setup.error)}</Alert> : null}
    </AuthLayout>
  );
}
